/**
 * absenceQuotaMessage — la phrase de l'encadré « Absences », et son ton.
 *
 * Une phrase par cas, et la raison quand l'absence ne sera pas rattrapée (Wajih, 24/10 : écart
 * voulu avec l'ancien libellé, qui comptait « N crédits d'absence sur les M autorisés sur une
 * période de 2 mois »). Ce qui compte pour la famille est ce qui reste, pas ce qui est consommé.
 * Alerte quand le quota est atteint, information sinon.
 *
 * Pur, à côté de la molécule qui le pose, pour que chaque phrase se teste (`absenceQuotaMessage.test.ts`).
 */
import { absenceQuotaAfterConfirm } from "../../../ports/primary/ui/formatters/absenceQuotaFormatter"
import { DEFAULT_CHILD_NAME } from "../../../ports/primary/ui/formatters/childFormatter"
import { type UIAbsenceNotRecoveredReason, type UIAbsenceQuota } from "../../../ports/primary/ui/types"
import { type Voice } from "../../utils/voice"

const NOT_RECOVERED = "Cette absence ne sera pas rattrapée"

/** Les raisons qui se disent pareil dans les deux voix. */
const SHARED_REASONS: Partial<Record<UIAbsenceNotRecoveredReason, string>> = {
    tooLate: `${NOT_RECOVERED} : c'est moins de 2 h avant la séance.`,
    alreadyRecovery: `${NOT_RECOVERED} : c'est déjà un rattrapage.`,
}

/** « 2 absences rattrapables », « 1 absence rattrapable », « 0 absence rattrapable ». */
const recoverableAbsences = (count: number) =>
    count > 1 ? `${count} absences rattrapables` : `${count} absence rattrapable`

const TEXTS: Record<
    Voice,
    {
        counted: (remaining: number, quota: UIAbsenceQuota, child: string) => string
        reached: (quota: UIAbsenceQuota, child: string) => string
    }
> = {
    parent: {
        counted: (remaining, quota, child) =>
            `Cette absence sera rattrapée. Il restera à ${child} ${recoverableAbsences(remaining)} sur ${quota.limit} sur ${quota.windowMonths} mois.`,
        reached: (quota, child) =>
            `${NOT_RECOVERED} : ${child} a déjà utilisé ${quota.limit > 1 ? `ses ${quota.limit} absences rattrapables` : "sa seule absence rattrapable"} sur ${quota.windowMonths} mois.`,
    },
    student: {
        counted: (remaining, quota) =>
            `Cette absence sera rattrapée. Il t'en restera ${remaining} sur ${quota.limit} sur ${quota.windowMonths} mois.`,
        reached: (quota) =>
            `${NOT_RECOVERED} : tu as déjà utilisé ${quota.limit > 1 ? `tes ${quota.limit} absences rattrapables` : "ta seule absence rattrapable"} sur ${quota.windowMonths} mois.`,
    },
}

export function absenceQuotaMessage(
    quota: UIAbsenceQuota,
    voice: Voice,
    childFirstname?: string
): { severity: "info" | "alert"; message: string } {
    const { counted, remaining, reached, reason } = absenceQuotaAfterConfirm(quota)
    const child = childFirstname?.trim() || DEFAULT_CHILD_NAME
    const texts = TEXTS[voice]
    const message = counted
        ? texts.counted(remaining, quota, child)
        : reason === "reached"
          ? texts.reached(quota, child)
          : ((reason != null ? SHARED_REASONS[reason] : undefined) ?? `${NOT_RECOVERED}.`)
    return { severity: reached ? "alert" : "info", message }
}
